import React from 'react';
import Block from './Block';
import { Badge } from '../../components/ui/badge';
import { GRADE_STYLES, GradeBadge, readable } from './CausalGrade';

export { GRADE_STYLES };
export const mono = { fontVariantNumeric: 'tabular-nums' };

export const fmt = (x, scale = 1) => {
  if (x === null || x === undefined) return '—';
  const v = x * scale;
  const abs = Math.abs(v);
  if (abs >= 100) return v.toFixed(0);
  if (abs >= 1) return v.toFixed(2);
  return v.toPrecision(3);
};

// display_scale === 100 is the backend's only "this is a percentage" signal
// (binary outcomes) — fmt itself never appends a unit.
const pctSuffix = (scale) => (scale === 100 ? '%' : '');
const fmtPct = (x, scale) => (x === null || x === undefined ? '—' : `${fmt(x, scale)}${pctSuffix(scale)}`);
// One-decimal formatting for the projection hero/delta ("20.9%", "+6.6%") —
// sub-lines and CIs keep fmt's full precision.
const fmtHero = (x, scale) => {
  if (x === null || x === undefined) return '—';
  const v = x * scale;
  const abs = Math.abs(v);
  const s = abs >= 100 ? v.toFixed(0) : abs >= 0.1 ? v.toFixed(1) : v.toPrecision(2);
  return `${s}${pctSuffix(scale)}`;
};

const sign = (v) => (v > 0 ? '+' : '');
// Green for a positive change, red otherwise.
export const toneOf = (v) => (v > 0 ? 'success' : 'destructive');

// One KPI tile, the same q-stat the forecast and response-curve blocks use.
const Stat = ({ label, value, badge, badgeTone, muted, hint }) => (
  <div className="q-stat">
    <span className="q-stat__label">{label}</span>
    <span className="q-stat__value" style={muted ? { color: 'var(--muted-foreground)' } : undefined}>
      {value}
      {badge && <Badge variant={badgeTone || 'outline'} className="ui-badge--mono">{badge}</Badge>}
    </span>
    {hint && <span className="q-stat__hint">{hint}</span>}
  </div>
);

// The what-if: the projected outcome leads with its change as a badge, the
// per-unit effect sits beside it, and the scenario it assumes reads underneath.
const ProjectionTiles = ({ projection, effect, scale, treatmentCol, significant }) => {
  const { treatment: tr, delta, base, projected } = projection;
  const step = `${tr.effective_delta >= 0 ? '+' : ''}${fmt(tr.effective_delta)}`;
  let scenario = null;
  if (tr.target_pct != null) scenario = `${tr.target_pct >= 0 ? '+' : ''}${fmt(tr.target_pct)}% of current ${fmt(tr.baseline_level)} → ${step}`;
  else if (tr.mode === 'absolute') scenario = `target ${fmt(tr.target)}, currently ${fmt(tr.baseline_level)} → ${step}`;

  return (
    <>
      <div className="q-stats">
        <Stat
          label="Causal projection"
          value={fmtHero(projected.point, scale)}
          badge={`${sign(delta.point)}${fmtHero(delta.point, scale)}`}
          badgeTone={toneOf(delta.point)}
          muted={!significant}
          hint={`95% CI ${fmtPct(projected.ci_low, scale)} to ${fmtPct(projected.ci_high, scale)}`}
        />
        <Stat
          label={`Per unit of ${treatmentCol}`}
          value={`${sign(effect.point)}${fmt(effect.point, scale)}`}
          hint={`95% CI ${fmt(effect.ci_low, scale)} to ${fmt(effect.ci_high, scale)}`}
        />
      </div>
      <p className="q-causal__context">
        from {fmtPct(base.value, scale)} current avg
        {scenario && <> · {scenario}</>}
        {projection.filter && <> · filter: {projection.filter}</>}
      </p>
    </>
  );
};

// The effect as two bars: the outcome without the treatment and with it.
// The without level follows from the effect and its relative size.
const WithWithout = ({ effect, scale, treatment }) => {
  const rel = effect.relative_pct;
  if (rel == null || !rel || effect.point == null) return null;
  const without = effect.point / (rel / 100);
  const withT = without + effect.point;
  if (!Number.isFinite(without) || without <= 0 || withT <= 0) return null;
  const max = Math.max(without, withT);
  const rows = [
    { key: 'without', label: `Without ${treatment || 'treatment'}`, value: without, tone: 'var(--q-hair-strong, #cfcfd6)' },
    { key: 'with', label: `With ${treatment || 'treatment'}`, value: withT, tone: 'var(--chart-1)' },
  ];
  return (
    <div className="q-causal__compare">
      {rows.map((r) => (
        <div key={r.key} className="q-causal__compare-row">
          <span className="q-causal__compare-label">{r.label}</span>
          <div className="q-causal__compare-track">
            <div className="q-causal__compare-bar" style={{ width: `${(r.value / max) * 100}%`, background: r.tone }} />
          </div>
          <span className="q-causal__compare-value q-num">{fmt(r.value, scale)}</span>
        </div>
      ))}
    </div>
  );
};

/**
 * Causal effect estimate with diagnostics, robustness checks, and caveats.
 * Props: blockData = causal_estimate payload from tableyze.causal.reporting.
 */
const CausalEffectBlock = ({ blockData }) => {
  const data = blockData || {};
  const effect = data.effect || {};
  const scale = effect.display_scale || 1;
  const confidence = data.confidence || {};
  const diagnostics = data.diagnostics || {};
  // Only a genuinely severe identification problem (VIF >= 10) earns the
  // prominent warning box; moderate ("weak") collinearity is left to the
  // confidence grade and caveats, since the CI already reflects it.
  const identification = diagnostics.identification;
  const identWarning = identification && identification.severity === 'severe'
    ? identification
    : null;
  // Only the current shape gets the projection tile. Legacy payloads in chat
  // history (delta_total/baseline_outcome/projected_outcome) fall back to the
  // per-unit hero below — their old "projected total" numbers were wrong, so
  // re-rendering them would be dishonest.
  const rawProjection = effect.projection;
  const projection = rawProjection && rawProjection.projected && rawProjection.base && rawProjection.delta
    ? rawProjection
    : null;
  const significant = projection
    ? !(projection.delta.ci_low <= 0 && projection.delta.ci_high >= 0)
    : !(effect.ci_low <= 0 && effect.ci_high >= 0);
  const sample = data.sample || {};
  const treatmentCol = (data.treatment || {}).column;
  const outcomeCol = (data.outcome || {}).column;
  const description = [treatmentCol && outcomeCol ? `${readable(treatmentCol)} → ${readable(outcomeCol)}` : null,
    data.method ? readable(data.method) : null].filter(Boolean).join(' · ');

  return (
    <Block
      icon="flask-conical"
      title="Causal effect"
      description={description || null}
      badge={confidence.grade ? <GradeBadge grade={confidence.grade} /> : null}
    >
      {projection ? (
        <ProjectionTiles projection={projection} effect={effect} scale={scale}
                         treatmentCol={treatmentCol} significant={significant} />
      ) : (
        <>
          <div className="q-stats">
            <Stat
              label="Estimated effect"
              value={`${sign(effect.point)}${fmt(effect.point, scale)}`}
              badge={effect.relative_pct !== null && effect.relative_pct !== undefined
                ? `${sign(effect.relative_pct)}${effect.relative_pct}% vs baseline` : null}
              badgeTone={effect.relative_pct != null ? toneOf(effect.relative_pct) : null}
              muted={!significant}
              hint={`95% CI ${fmt(effect.ci_low, scale)} to ${fmt(effect.ci_high, scale)}`}
            />
            {sample.n && (
              <Stat label="Sample" value={sample.n.toLocaleString()} hint="rows analysed" />
            )}
            {sample.n && sample.n_treated && (
              <Stat
                label={`Used ${readable(treatmentCol) || 'treatment'}`.replace(/^Used used /, 'Used ')}
                value={sample.n_treated.toLocaleString()}
                hint={`${((sample.n_treated / sample.n) * 100).toFixed(0)}% of the sample`}
              />
            )}
          </div>
          <p className="q-causal__context">{readable(effect.units)}</p>
          <WithWithout effect={effect} scale={scale} treatment={readable(treatmentCol)} />
        </>
      )}

      {identWarning && (
        <div className="q-causal__warning">
          <span aria-hidden>⚠</span>
          <div>
            <strong>Weakly identified effect</strong>
            <br />
            {identWarning.message}
          </div>
        </div>
      )}
    </Block>
  );
};

export default CausalEffectBlock;
